import React, { useEffect, useRef, useState } from "react";
import { initHighchartsModules } from "../../../../../utils/initHighchartsModules";
import {
  Button,
  Card,
  Col,
  Dropdown,
  DropdownButton,
  Row,
} from "react-bootstrap";
import { fetchInvoiceReportsData } from "../../../../../services/BillingDashboardAPI";
import AccordionComponent from "../../../../../common/Accordion";

interface InvoiceReportsProps {
  openDropdown: string | null;
  setOpenDropdown: React.Dispatch<React.SetStateAction<string | null>>;
  filters: {
    teamId: string | number | null;
    clientId: string | number | null;
    currencyType: string;
    yearMonth: Date | null;
  };
}

const InvoiceReportsSummaryData: React.FC<InvoiceReportsProps> = ({
  openDropdown,
  setOpenDropdown,
  filters,
}) => {
  const [summaryData, setSummaryData] = useState<any | null>(null);

  useEffect(() => {
    initHighchartsModules();
  }, []);

  const formatYearMonth = (date: Date | null) => {
    if (!date) return null;
    return date.toISOString().slice(0, 7); // YYYY-MM
  };

  const fetchLineItemPipelineData = async () => {
    try {
      const payload = {
        teamId: filters.teamId,
        clientId: filters.clientId,
        currencyType: filters.currencyType,
        yearMonth: formatYearMonth(filters.yearMonth),
      };

      const result = await fetchInvoiceReportsData(payload);
      setSummaryData(result?.result);
    } catch (error) {
      console.error("Failed to load case overview data", error);
    }
  };

  useEffect(() => {
    fetchLineItemPipelineData();
  }, [filters]);

  const [caseSummaryKey, setCaseSummaryKey] = useState<string | null>("0");

  const handleBodyAccordionSelect = (key: string | null) => {
    if (caseSummaryKey === key) {
      return;
    }
    setCaseSummaryKey(key || "0");
  };

  const summaryAccordion = (
    items: { title: JSX.Element; content: JSX.Element }[],
  ) => (
    <AccordionComponent
      items={items.map((item, index) => ({
        ...item,
      }))}
      activeKey={caseSummaryKey}
      onSelect={handleBodyAccordionSelect}
    />
  );

  const parsedData = summaryData?.statusCounts
    ? JSON.parse(summaryData.statusCounts)
    : {};

  const CARD_ORDER = [
    "Total Invoice Generated",
    "Pending Invoice Line Items",
    "Canceled Invoice",
    "Canceled Invoice Line Items",
  ];

  const CARD_CONFIG: Record<
    string,
    {
      icon: string;
      bg: string;
      iconColor: string;
      amountColor: string;
      label: string;
    }
  > = {
    "Total Invoice Generated": {
      icon: "auto_stories",
      bg: "#e6f0ff",
      iconColor: "#2566e8",
      amountColor: "#008cd3",
      label: "Invoice",
    },
    "Pending Invoice Line Items": {
      icon: "menu",
      bg: "#dbe3b8",
      iconColor: "#a3b265",
      amountColor: "#008cd3",
      label: "Line Items",
    },
    "Canceled Invoice": {
      icon: "auto_stories",
      bg: "#ffdfda",
      iconColor: "#e75042",
      amountColor: "#ea6458",
      label: "Invoice",
    },
    "Canceled Invoice Line Items": {
      icon: "menu",
      bg: "#ffdfda",
      iconColor: "#e75042",
      amountColor: "#ea6458",
      label: "Line Items",
    },
  };

  const cards = CARD_ORDER.map((key) => {
    return {
      title: key,
      value: parsedData[key] || { count: 0, amount: 0 },
    };
  });
  return (
    <>
      {summaryAccordion([
        {
          title: (
            <div className="d-flex justify-content-between align-items-center w-100 p-0">
              <span className="">Invoice Reports</span>
            </div>
          ),
          content: (
            <div>
              <Row className="g-3">
                {cards.map(({ title, value }, index) => {
                  const config = CARD_CONFIG[title] || {};

                  return (
                    <Col md={6} key={index}>
                      <div
                        className="invoice-card case-action-box p-3"
                        style={{ position: "relative" }}
                      >
                        {/* Icon */}
                        <div
                          style={{
                            position: "absolute",
                            top: "10px",
                            right: "10px",
                            background: config.bg,
                            borderRadius: "6px",
                            padding: "6px",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                          }}
                        >
                          <span
                            className="material-symbols-outlined"
                            style={{
                              fontSize: "18px",
                              color: config.iconColor,
                            }}
                          >
                            {config.icon}
                          </span>
                        </div>

                        <div className="invoice-content d-flex align-items-center">
                          <div className="flex-grow-1">
                            <h5 className="mb-2 fw-semibold">{title} </h5>

                            <div className="d-flex align-items-center">
                              <div className="pe-3">
                                <div className="small text-muted">
                                  {config.label}
                                </div>
                                <h5
                                  className="fw-bold mb-0"
                                  style={{ color: "#018901" }}
                                >
                                  {value.count}
                                </h5>
                              </div>

                              <div
                                style={{
                                  width: "1px",
                                  height: "35px",
                                  backgroundColor: "#ddd",
                                }}
                              />

                              <div className="ps-3">
                                <div className="small text-muted">Amount</div>
                                <h5
                                  className="fw-bold mb-0"
                                  style={{ color: config.amountColor }}
                                >
                                  {filters.currencyType === "INR" ? "₹ " : "$ "}
                                  {Number(value.amount || 0).toLocaleString(
                                    "en-US",
                                    {
                                      minimumFractionDigits: 2,
                                      maximumFractionDigits: 2,
                                    },
                                  )}
                                </h5>
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                    </Col>
                  );
                })}
              </Row>
            </div>
          ),
        },
      ])}
    </>
  );
};

export default InvoiceReportsSummaryData;
